import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { api } from "@/api";
import type { LicenseInfo, LicenseStatus } from "@/types";
import {
  createCacheState,
  isCacheFresh,
  withInFlight,
  DEFAULT_TTL_MS,
} from "@/stores/shared/cache";
import { isAbortError } from "@/utils/abort";

/** Days before `until` at which the persistent top warning bar appears. */
export const EXPIRY_WARNING_DAYS = 14;

/** Days before `until` at which a modal interrupts on top of the bar. */
export const EXPIRY_MODAL_DAYS = 7;

/**
 * How long a failed load is remembered before another request is allowed.
 * App.vue reloads on every route change, so without this a broken endpoint
 * would get one request per navigation for the whole session.
 */
export const ERROR_RETRY_TTL_MS = 30 * 1000;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole calendar days from `now` until `until` (RFC3339), `null` when there is
 * no parseable expiry. Rounded away from zero rather than with `Math.ceil`: a
 * license that died a few hours ago must not collapse to `-0`, which is not
 * `< 0` and would leave it reading as still alive.
 */
export function daysUntil(
  until: string,
  now: number = Date.now(),
): number | null {
  if (!until) return null;
  const ts = Date.parse(until);
  if (Number.isNaN(ts)) return null;
  const diff = ts - now;
  if (diff < 0) return -Math.ceil(-diff / MS_PER_DAY);
  return Math.ceil(diff / MS_PER_DAY);
}

export function licenseStatus(days: number | null): LicenseStatus {
  if (days === null) return "valid"; // no known expiry → treat as valid
  if (days < 0) return "expired";
  if (days <= EXPIRY_WARNING_DAYS) return "expiring_soon";
  return "valid";
}

export const useLicenseStore = defineStore("settings.license", () => {
  const info = ref<LicenseInfo | null>(null);
  const cache = createCacheState();
  const status = ref<"idle" | "loading" | "ready" | "error">("idle");

  const lastFailed = ref<number | null>(null);
  let lastError: unknown = null;

  const isLoading = computed(() => status.value === "loading");

  const daysLeft = computed(() =>
    info.value ? daysUntil(info.value.until) : null,
  );
  const licenseState = computed<LicenseStatus>(() =>
    licenseStatus(daysLeft.value),
  );

  const contactEmail = computed(() => info.value?.email ?? "");

  const isExpired = computed(() => licenseState.value === "expired");

  const isExpiringSoon = computed(() => licenseState.value === "expiring_soon");

  /** Inside the tighter modal window, and not yet expired. */
  const isCritical = computed(() => {
    const d = daysLeft.value;
    return d !== null && d >= 0 && d <= EXPIRY_MODAL_DAYS;
  });

  const isUnlimitedAgents = computed(() => (info.value?.agents ?? 0) === 0);

  const agentsUsageRatio = computed(() => {
    if (!info.value || info.value.agents <= 0) return null;
    return info.value.agents_count / info.value.agents;
  });

  async function load(options?: { force?: boolean; signal?: AbortSignal }) {
    if (!options?.force) {
      if (info.value && isCacheFresh(cache.lastFetched, DEFAULT_TTL_MS)) {
        return info.value;
      }
      // Replayed rather than swallowed, so callers still render their error
      // state without a request; `force` (the retry button) always hits network.
      if (isCacheFresh(lastFailed.value, ERROR_RETRY_TTL_MS)) throw lastError;
    }
    return withInFlight(cache, "license", async () => {
      status.value = "loading";
      try {
        const data = await api.getLicenseInfo(options?.signal);
        info.value = data;
        cache.lastFetched = Date.now();
        lastFailed.value = null;
        lastError = null;
        status.value = "ready";
        return data;
      } catch (err) {
        // Backing off on an abort would suppress the next legitimate attempt.
        if (!isAbortError(err)) {
          lastFailed.value = Date.now();
          lastError = err;
        }
        status.value = "error";
        throw err;
      }
    });
  }

  function reset() {
    info.value = null;
    cache.lastFetched = null;
    cache.inFlight.clear();
    lastFailed.value = null;
    lastError = null;
    status.value = "idle";
  }

  return {
    info,
    status,
    isLoading,
    daysLeft,
    licenseState,
    contactEmail,
    isExpired,
    isExpiringSoon,
    isCritical,
    isUnlimitedAgents,
    agentsUsageRatio,
    load,
    reset,
  };
});
